import { ISSUE_ATTRIBUTE_NAMES } from '../../constants/issue-attribute-names';
import { CONSTANT_MANDATORY_FIELDS } from '../../hooks/useManageFields';

/**
 * Empty value to use when clearing each standard field. Centralized to avoid
 * accidentally producing `undefined` (Formik treats undefined as "field
 * untouched" which leaves stale `formik.values`).
 */
const EMPTY_VALUE_BY_FIELD: Record<string, () => unknown> = {
  [ISSUE_ATTRIBUTE_NAMES.DISCIPLINE]: () => [],
  [ISSUE_ATTRIBUTE_NAMES.CATEGORY]: () => [],
  [ISSUE_ATTRIBUTE_NAMES.LOCATION_IDS]: () => [],
  [ISSUE_ATTRIBUTE_NAMES.CO_REVIEWERS]: () => [],
  [ISSUE_ATTRIBUTE_NAMES.WATCHERS]: () => [],
  [ISSUE_ATTRIBUTE_NAMES.PRIORITY]: () => null,
  [ISSUE_ATTRIBUTE_NAMES.COST]: () => null,
  [ISSUE_ATTRIBUTE_NAMES.SCHEDULE]: () => null,
};

const emptyValueFor = (fieldName: string): unknown => {
  const factory = EMPTY_VALUE_BY_FIELD[fieldName];
  return factory ? factory() : '';
};

export type SetFieldValueFn = (field: string, value: unknown, shouldValidate?: boolean) => void;

export type ClearHiddenFieldValuesParams = {
  /** Names of standard fields the active manageFields marks `isVisible: false`. */
  hiddenStandardNames: string[];
  /** UUIDs of custom attributes the active manageFields marks `isVisible: false`. */
  hiddenCustomIds: string[];
  /** Current Formik values (read for `customAttributes` filtering). */
  values: Record<string, unknown> & {
    customAttributes?: Array<{ id: string; values: Array<string | null> }>;
  };
  setFieldValue: SetFieldValueFn;
};

/**
 * Resets values of standard + custom-attribute fields that the new manageFields
 * marks as hidden. Called on RFI-type change so that re-showing a previously
 * hidden field starts from a clean state — and so a hidden field's value never
 * sneaks into the submission payload.
 *
 * Safety: this helper never touches the five `CONSTANT_MANDATORY_FIELDS`,
 * regardless of what `hiddenStandardNames` contains, because the form always
 * renders those.
 */
export const clearHiddenFieldValues = ({
  hiddenStandardNames,
  hiddenCustomIds,
  values,
  setFieldValue,
}: ClearHiddenFieldValuesParams): void => {
  hiddenStandardNames.forEach((fieldName) => {
    if (CONSTANT_MANDATORY_FIELDS.includes(fieldName)) return;
    setFieldValue(fieldName, emptyValueFor(fieldName), false);
  });

  if (hiddenCustomIds.length === 0) return;
  const currentCustomAttributes = values.customAttributes ?? [];
  const filtered = currentCustomAttributes.filter((attr) => !hiddenCustomIds.includes(attr.id));
  if (filtered.length !== currentCustomAttributes.length) {
    setFieldValue(ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES, filtered, false);
  }
};
